/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {ValidationModel} from './validation.model';

export interface IoMatrixCircuit {
  circNO?: string;
  circSig?: string;
  circSubsys?: string;
  circScope?: {
    circDesc?: string;
  };
}

export interface IoMatrixDeviceIo {
  ioDeviceName?: string;
  devicePinName?: string;
  deviceIOName?: string;
  deviceIOType?: string;
  deviceIOValue?: string;
  ioTwist?: string;
  ioShield?: string;
  networkGroup?: string;
  pinType?: string;
  ociAcronym?: string;
  circuit?: IoMatrixCircuit;
}

export interface IoMatrixDriftSummary {
  matchedCount?: number;
  changedCount?: number;
  deletedCount?: number;
  newCount?: number;
  driftDetected?: boolean;
}

export interface IoMatrixLoadResponse {
  rows: IoMatrixRow[];
  driftSummary?: IoMatrixDriftSummary;
  liveDeviceIoRows?: IoMatrixRow[];
}

export type IoMatrixView = 'architecture' | 'vios' | 'hardware';

export const IO_MATRIX_ORIGINAL_VARIANT = '';
export const IO_MATRIX_ORIGINAL_VARIANT_LABEL = 'Original / No Variant';
export const IO_MATRIX_VARIANT_DELIMITER = '|';

/** User-facing name for the powertrain IO Matrix page (distinct from future Vehicle IO Matrix). */
export const POWERTRAIN_IO_MATRIX_DISPLAY_NAME = 'Powertrain IO Matrix';

export function variantTokenPartsAreValid(usage: string, partNumber: string): boolean {
  const delimiter = IO_MATRIX_VARIANT_DELIMITER;
  return !(usage ?? '').includes(delimiter) && !(partNumber ?? '').includes(delimiter);
}

/** Normalizes IO Matrix / ICD template scope fields so null/undefined never become "undefined" in UI. */
export function normalizeIoMatrixScopeField(value: string | null | undefined): string {
  return (value ?? '').trim();
}

export function formatIoMatrixScopeLabel(
  objectCode: string | null | undefined,
  objectCodeInst: string | null | undefined,
  architecture: string | null | undefined,
  cadenceVer: string | null | undefined
): string {
  return [
    normalizeIoMatrixScopeField(objectCode),
    normalizeIoMatrixScopeField(objectCodeInst),
    normalizeIoMatrixScopeField(architecture),
    normalizeIoMatrixScopeField(cadenceVer)
  ].join(' | ');
}

export function normalizeIoMatrixSnapshot(snapshot: IoMatrixSnapshot): IoMatrixSnapshot {
  return {
    matrixId: normalizeIoMatrixScopeField(snapshot.matrixId),
    objectCode: normalizeIoMatrixScopeField(snapshot.objectCode),
    objectCodeInst: normalizeIoMatrixScopeField(snapshot.objectCodeInst),
    architecture: normalizeIoMatrixScopeField(snapshot.architecture),
    cadenceVer: normalizeIoMatrixScopeField(snapshot.cadenceVer),
    rowCount: snapshot.rowCount ?? 0,
    inUse: snapshot.inUse ?? false
  };
}

export function normalizeIcdTemplateInventory(template: IcdTemplateInventory): IcdTemplateInventory {
  return {
    ...template,
    objectCode: normalizeIoMatrixScopeField(template.objectCode),
    objectCodeInst: normalizeIoMatrixScopeField(template.objectCodeInst),
    architecture: normalizeIoMatrixScopeField(template.architecture),
    cadenceVer: normalizeIoMatrixScopeField(template.cadenceVer),
    program: normalizeIoMatrixScopeField(template.program),
    modelYear: normalizeIoMatrixScopeField(template.modelYear),
    rpo: normalizeIoMatrixScopeField(template.rpo),
    additionalRpos: normalizeIoMatrixScopeField(template.additionalRpos),
    buildPhase: normalizeIoMatrixScopeField(template.buildPhase),
    variant: normalizeIoMatrixScopeField(template.variant)
  };
}

export interface IoMatrixSnapshot {
  matrixId: string;
  objectCode: string;
  objectCodeInst: string;
  architecture: string;
  cadenceVer: string;
  rowCount: number;
  inUse: boolean;
}

export interface IcdRefMapStatus {
  id?: number;
  objectCode?: { code?: string };
  objectCodeInst?: { inst?: string };
  vpps?: { vpps?: string };
  upc?: { upcCode?: string };
  fna?: { fnaCode?: string };
}

export class IoMatrixRow extends ValidationModel {
  id?: number;
  objectCode: string;
  objectCodeInst?: string;
  fkControllerDesignatorId?: number;
  architecture: string;
  cadenceVer: string;
  connectorAndPin: string;
  viosSignalName?: string;
  viosSignalClass?: string;
  viosSignalInterface?: string;
  hwControllerPinName?: string;
  hwPinAbbreviation?: string;
  hwPinClass?: string;
  hwPinElectricalInterface?: string;
  variant?: string;
  mergeStatus?: string;
  fkNetRptIoMapId?: number | null;
  /** Transient same-cadence drift metadata from /rows (not persisted). */
  mergeMode?: 'matched' | 'changed' | 'new' | 'deleted';
  liveDeviceIo?: IoMatrixDeviceIo;
  deviceIo: IoMatrixDeviceIo;
  icdCircuitSelection?: string;
    fieldRules = [
        {
            fieldName: 'connectorAndPin',
            maxLength: 160,
            required: false,
            pattern: /^$|^[A-Za-z0-9]+-[A-Za-z0-9]+$/,
            patternName: '<connector>-<pin> (alphanumeric connector and pin)',
            forErrorMessage: 'Connector and Pin'
        }
    ];
}

export interface IcdTemplateInventory {
  id: number;
  templateId?: number;
  objectCode: string;
  objectCodeInst: string;
  architecture: string;
  cadenceVer: string;
  program: string;
  modelYear: string;
  rpo: string;
  additionalRpos?: string;
  buildPhase: string;
  variant?: string;
  createdBy: string;
  createdDate: string;
  updatedBy: string;
  updatedDate: string;
  pinCount?: number;
  assignableRowCount?: number;
}

export interface CreateIcdsFromTemplatesResponse {
  createdIcdIds: number[];
  singleIcdDestination?: string;
}

export interface IoMatrixSnapshotOption {
  label: string;
  key: string;
  snapshot: IoMatrixSnapshot;
}

export interface IoMatrixDeviceNameBanner {
  display: string;
  tooltip: string;
}

/**
 * Resolves the IO Matrix device-name banner above the grid. A single shared name is shown
 * verbatim; multiple distinct names collapse to {@code multiple} with a tooltip listing
 * the sorted unique values.
 */
export function resolveIoMatrixDeviceNameBanner(
  rows: IoMatrixRow[] | null | undefined,
  hasMatrixContext: boolean
): IoMatrixDeviceNameBanner {
  if (!hasMatrixContext || !rows?.length) {
    return {display: '', tooltip: ''};
  }
  const deviceNames = new Set<string>();
  for (const row of rows) {
    const deviceName = String(row?.deviceIo?.ioDeviceName ?? '').trim();
    if (deviceName) {
      deviceNames.add(deviceName);
    }
  }
  if (deviceNames.size === 0) {
    return {display: '', tooltip: ''};
  }
  if (deviceNames.size === 1) {
    return {display: [...deviceNames][0], tooltip: ''};
  }
  const sortedNames = [...deviceNames].sort((left, right) =>
    left.localeCompare(right, undefined, {sensitivity: 'base'}));
  return {display: 'multiple', tooltip: sortedNames.join(', ')};
}

/**
 * Shared "snapshot -> dropdown option" projection used by `IoMatrixComponent` and
 * `IoMatrixIcdsComponent` (and any future page that lists saved IO Matrix snapshots in a
 * dropdown). Sorts by `${matrixId}|${cadenceVer}` for stable ordering and produces the
 * canonical `objectCode | objectCodeInst | architecture | cadenceVer` label so dropdowns
 * stay visually consistent across pages.
 */
export function buildIoMatrixSnapshotOptions(
  snapshots: IoMatrixSnapshot[] | null | undefined
): IoMatrixSnapshotOption[] {
  return [...(snapshots || [])]
    .sort((a, b) => `${a?.matrixId ?? ''}|${a?.cadenceVer ?? ''}`
      .localeCompare(`${b?.matrixId ?? ''}|${b?.cadenceVer ?? ''}`))
    .map(rawSnapshot => {
      const snapshot = normalizeIoMatrixSnapshot(rawSnapshot);
      return {
        label: formatIoMatrixScopeLabel(
          snapshot.objectCode,
          snapshot.objectCodeInst,
          snapshot.architecture,
          snapshot.cadenceVer
        ),
        key: `${snapshot.matrixId}|${snapshot.cadenceVer}`,
        snapshot
      };
    });
}

